/**
 * THE registry the builder reads — one import site for the whole app.
 *
 * It is `@aquilixshop/ui-react`'s LAZY registry, not the `defaultRegistry` the storefront renders from.
 * Same sixty-one sections, same schemas, same presets, same meta — all of it still synchronous, which is
 * what every call site here needs. The one difference is that a section's render code arrives on demand.
 *
 * Why: the eager registry pulls all sixty-one components, and through them every block, into whatever
 * bundle imports it. In Astro's Node process that is free. In the seller's browser it was 2.1 MB of the
 * 5.5 MB the builder's entry chunk is built from, for an editor that opens ONE page — and it is why the
 * builder sat on a spinner for twenty-five seconds before React mounted (preprod, 2026-09-16).
 *
 * ── The one rule ───────────────────────────────────────────────────────────────────────────────────────
 *
 * A document's sections must be LOADED before that document goes on screen, or the canvas paints blanks
 * where they should be. `loadSectionComponents(types)` is that await, and the load path already holds it
 * ({@link ../state/builderStore}): every route load, every seeded draft, every section inserted from the
 * picker. Reading `schema` / `presets` / `meta` needs no await and never did.
 *
 * Importing the registry from here rather than from the package barrel is also what keeps the eager one
 * out of the bundle: one grep for `defaultRegistry` in this app is the check.
 */
import {
  lazyRegistry,
  registerDefaultSectionRegistry,
} from '@aquilixshop/ui-react/lazy-registry';

// REGISTRATION, and it happens on import. `PageRenderer` and the default theme's seeds resolve their
// registry late (`getDefaultSectionRegistry`), which is what lets this app choose the lazy one without the
// eager one riding along in the bundle. Doing it here means every module that reads the registry has, by
// the act of importing it, made rendering work. Nothing in this app may import
// `@aquilixshop/ui-react/eager-registry` — that would ship all sixty-one components after all.
registerDefaultSectionRegistry(lazyRegistry);

export {
  isSectionComponentLoaded,
  lazyRegistry as sectionRegistry,
  lazySectionTypes,
  loadSectionComponents,
  warmAllSectionComponents,
} from '@aquilixshop/ui-react/lazy-registry';
